import type { SubscriptionProduct } from '@/api/products'
import {
  ListAction,
  PublishIssue,
  actionsForStatus,
  canPublishProduct,
  isFutureUtc,
  utcWall,
} from './rules'

export { ListAction, actionsForStatus }
export type { PublishIssue }

export const ListCommandType = {
  Edit: 'edit',
  Blocked: 'blocked',
  Duplicate: 'duplicate',
  Confirm: 'confirm',
} as const
export type ListCommandType = (typeof ListCommandType)[keyof typeof ListCommandType]

export const ConfirmApi = {
  Publish: 'publish',
  ListNow: 'listNow',
  Cancel: 'cancel',
  Unlist: 'unlist',
  Relist: 'relist',
  Delete: 'delete',
} as const
export type ConfirmApi = (typeof ConfirmApi)[keyof typeof ConfirmApi]

export const ConfirmTitle = {
  Schedule: 'products.confirmScheduleTitle',
  Publish: 'products.confirmPublishTitle',
  ListNow: 'products.confirmListNowTitle',
  Cancel: 'products.confirmCancelTitle',
  Unlist: 'products.confirmUnlistTitle',
  RelistSchedule: 'products.confirmRelistScheduleTitle',
  Relist: 'products.confirmRelistTitle',
  Delete: 'products.confirmDeleteTitle',
} as const

export const ConfirmBody = {
  Schedule: 'products.confirmScheduleBody',
  Publish: 'products.confirmPublishBody',
  ListNow: 'products.confirmListNowBody',
  Cancel: 'products.confirmCancelBody',
  Unlist: 'products.confirmUnlistBody',
  RelistSchedule: 'products.confirmRelistScheduleBody',
  Relist: 'products.confirmRelistBody',
  Delete: 'products.confirmDeleteBody',
} as const

export const ConfirmOk = {
  Schedule: 'products.scheduleAction',
  Publish: 'products.publish',
  ListNow: 'products.listNow',
  Cancel: 'products.confirmCancelOk',
  Unlist: 'products.confirmUnlistOk',
  RelistNow: 'products.relistNowAction',
  Delete: 'common.delete',
} as const

export const ConfirmCancel = {
  Back: 'products.confirmCancelBack',
  Cancel: 'common.cancel',
} as const

export const ConfirmSuccess = {
  Scheduled: 'products.scheduledOk',
  Published: 'products.publishedOk',
  ListNow: 'products.listNowOk',
  Cancel: 'products.cancelOk',
  Unlist: 'products.unlistOk',
  Relist: 'products.relistOk',
  Deleted: 'products.deleted',
} as const

export const LIST_ACTION_LABEL = {
  [ListAction.Edit]: 'common.edit',
  [ListAction.Publish]: 'products.publish',
  [ListAction.ListNow]: 'products.listNow',
  [ListAction.Cancel]: 'products.cancelSchedule',
  [ListAction.Relist]: 'products.relist',
  [ListAction.Unlist]: 'products.unlist',
  [ListAction.Duplicate]: 'products.duplicate',
  [ListAction.Delete]: 'common.delete',
} as const

export type ConfirmDialog = {
  titleKey: (typeof ConfirmTitle)[keyof typeof ConfirmTitle]
  bodyKey: (typeof ConfirmBody)[keyof typeof ConfirmBody]
  bodyParams?: { time: string }
  unlistAt?: string
  okKey: (typeof ConfirmOk)[keyof typeof ConfirmOk]
  cancelKey?: (typeof ConfirmCancel)[keyof typeof ConfirmCancel]
  danger: boolean
  successKey: (typeof ConfirmSuccess)[keyof typeof ConfirmSuccess]
  successParams?: { time: string }
}

export type ListCommand =
  | { type: typeof ListCommandType.Edit }
  | { type: typeof ListCommandType.Blocked; issue: PublishIssue }
  | { type: typeof ListCommandType.Duplicate }
  | {
      type: typeof ListCommandType.Confirm
      api: ConfirmApi
      dialog: ConfirmDialog
    }

function unlistNote(p: SubscriptionProduct) {
  return p.unlist_at ? utcWall(p.unlist_at) : undefined
}

function scheduleDialog(
  titleKey: ConfirmDialog['titleKey'],
  bodyKey: ConfirmDialog['bodyKey'],
  time: string,
  unlistAt: string | undefined,
  successKey: ConfirmDialog['successKey'],
): ConfirmDialog {
  return {
    titleKey,
    bodyKey,
    bodyParams: { time },
    unlistAt,
    okKey: ConfirmOk.Schedule,
    danger: false,
    successKey,
    successParams: { time },
  }
}

export function resolveListAction(
  action: ListAction,
  p: SubscriptionProduct,
  now = Date.now(),
): ListCommand {
  if (action === ListAction.Edit) return { type: ListCommandType.Edit }
  if (action === ListAction.Duplicate) return { type: ListCommandType.Duplicate }

  if (action === ListAction.Publish) {
    const issue = canPublishProduct(p, now)
    if (issue) return { type: ListCommandType.Blocked, issue }
    if (isFutureUtc(p.list_at, now) && p.list_at) {
      return {
        type: ListCommandType.Confirm,
        api: ConfirmApi.Publish,
        dialog: scheduleDialog(
          ConfirmTitle.Schedule,
          ConfirmBody.Schedule,
          utcWall(p.list_at),
          unlistNote(p),
          ConfirmSuccess.Scheduled,
        ),
      }
    }
    return {
      type: ListCommandType.Confirm,
      api: ConfirmApi.Publish,
      dialog: {
        titleKey: ConfirmTitle.Publish,
        bodyKey: ConfirmBody.Publish,
        unlistAt: unlistNote(p),
        okKey: ConfirmOk.Publish,
        danger: false,
        successKey: ConfirmSuccess.Published,
      },
    }
  }

  if (action === ListAction.ListNow) {
    return {
      type: ListCommandType.Confirm,
      api: ConfirmApi.ListNow,
      dialog: {
        titleKey: ConfirmTitle.ListNow,
        bodyKey: ConfirmBody.ListNow,
        okKey: ConfirmOk.ListNow,
        danger: false,
        successKey: ConfirmSuccess.ListNow,
      },
    }
  }

  if (action === ListAction.Cancel) {
    return {
      type: ListCommandType.Confirm,
      api: ConfirmApi.Cancel,
      dialog: {
        titleKey: ConfirmTitle.Cancel,
        bodyKey: ConfirmBody.Cancel,
        okKey: ConfirmOk.Cancel,
        cancelKey: ConfirmCancel.Back,
        danger: true,
        successKey: ConfirmSuccess.Cancel,
      },
    }
  }

  if (action === ListAction.Unlist) {
    return {
      type: ListCommandType.Confirm,
      api: ConfirmApi.Unlist,
      dialog: {
        titleKey: ConfirmTitle.Unlist,
        bodyKey: ConfirmBody.Unlist,
        okKey: ConfirmOk.Unlist,
        danger: true,
        successKey: ConfirmSuccess.Unlist,
      },
    }
  }

  if (action === ListAction.Relist) {
    if (isFutureUtc(p.list_at, now) && p.list_at) {
      return {
        type: ListCommandType.Confirm,
        api: ConfirmApi.Relist,
        dialog: scheduleDialog(
          ConfirmTitle.RelistSchedule,
          ConfirmBody.RelistSchedule,
          utcWall(p.list_at),
          unlistNote(p),
          ConfirmSuccess.Scheduled,
        ),
      }
    }
    return {
      type: ListCommandType.Confirm,
      api: ConfirmApi.Relist,
      dialog: {
        titleKey: ConfirmTitle.Relist,
        bodyKey: ConfirmBody.Relist,
        unlistAt: unlistNote(p),
        okKey: ConfirmOk.RelistNow,
        danger: false,
        successKey: ConfirmSuccess.Relist,
      },
    }
  }

  return {
    type: ListCommandType.Confirm,
    api: ConfirmApi.Delete,
    dialog: {
      titleKey: ConfirmTitle.Delete,
      bodyKey: ConfirmBody.Delete,
      okKey: ConfirmOk.Delete,
      danger: true,
      successKey: ConfirmSuccess.Deleted,
    },
  }
}
